import React, { useState } from 'react';
import {
  Menu,
  MenuItem,
  Typography,
  Box,
} from '@mui/material';
import { KeyboardArrowDown } from '@mui/icons-material';
import { useLocalization } from '../contexts/LocalizationContext';

interface LanguageOption {
  code: 'en' | 'ja' | 'cn' | 'tc';
  name: string;
  nativeName: string;
}

const languages: LanguageOption[] = [
  { code: 'en', name: 'English', nativeName: 'English' },
  { code: 'ja', name: 'Japanese', nativeName: '日本語' },
  { code: 'tc', name: 'Traditional Chinese', nativeName: '繁體中文' },
  { code: 'cn', name: 'Simplified Chinese', nativeName: '簡體中文' },
];

interface Props {
  dropdownColor?: string;
}

export const LanguageSelector: React.FC<Props> = ({ dropdownColor }) => {
  const { locale, setLocale, getCurrentLanguageCode } = useLocalization();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageSelect = (languageCode: 'en' | 'ja' | 'cn' | 'tc') => {
    setLocale(languageCode);
    handleClose();
  };

  return (
    <>
      <Box
        onClick={handleClick}
        sx={{
          backgroundColor: dropdownColor ? 'transparent' : 'white',
          borderRadius: '8px',
          px: 2,
          py: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          cursor: 'pointer',
          minWidth: '80px',
          border: dropdownColor ? `1px solid ${dropdownColor}` : 'none',
          '&:hover': {
            backgroundColor: dropdownColor ? 'rgba(255,255,255,0.1)' : '#f5f5f5',
          },
          '&.MuiButtonBase-root': {
            backgroundColor: '#f5f5f5',
          },
        }}
      >
        <Typography
          sx={{
            fontSize: '14px',
            fontWeight: 500,
            color: dropdownColor || '#000',
          }}
        >
          {getCurrentLanguageCode()}
        </Typography>
        <KeyboardArrowDown sx={{ fontSize: '20px', color: dropdownColor || '#666' }} />
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          sx: {
            backgroundColor: 'white',
            borderRadius: '8px',
            mt: 1,
            minWidth: '120px',
          },
        }}
      >
        {languages.map((language) => (
          <MenuItem
            key={language.code}
            onClick={() => handleLanguageSelect(language.code)}
            selected={locale === language.code}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              py: 1,
              px: 2,
              backgroundColor: locale === language.code ? '#f5f5f5' : 'transparent',
              '&:hover': {
                backgroundColor: locale === language.code ? '#cfcfcfff' : '#eaeaeaff',
              },
              '&.Mui-selected': {
                backgroundColor: '#f5f5f5 !important',
              },
              '&.Mui-selected:hover': {
                backgroundColor: '#cfcfcfff !important',
              },
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#000' }}>
              {language.nativeName}
            </Typography>
            <Typography variant="caption" sx={{ color: '#666', fontSize: '10px' }}>
              {language.name}
            </Typography>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};